import { createSelector } from 'reselect';
import { FeatureFlags, GeoZone, OrgInfo, OrgLevel0, ZoneType } from '@fullstory/client-api';
import { BasePlan, PlanStage, Role } from '@fullstory/proto/fs/services/orgs';
import { AppState } from '../../types';
import { UserInfo } from './types';
import { orgAdminInfo } from '../settings/profile/selectors';
import { PrivacyFlags } from '@fullstory/client-api/goimports/fs_shared_privacy';

export const getOrgId = (state: AppState): string => state.fullstory.orgId;

export const getOrgName = (state: AppState): string =>
  orgAdminInfo(state)?.Name ?? state.fullstory.orgName;

export const getOrgStage = (state: AppState): PlanStage => state.fullstory.orgStage;

export const getBasePlan = (state: AppState): BasePlan => state.fullstory.orgBasePlan;

export const getOrgLevel0 = (state: AppState): OrgLevel0 | null => state.fullstory.orgLevel0;

export const getOrgIndustry = (state: AppState): string => state.fullstory.orgIndustry;

export const getTrialExpiration = (state: AppState): number | null =>
  state.fullstory.trialExpiration;

export const getOrgCreatedDate = (state: AppState): number | null => state.fullstory.orgCreatedDate;

export const getUserInfo = (state: AppState): UserInfo => state.fullstory.userInfo;

export const getAllOrgs = (state: AppState): OrgInfo[] => state.fullstory.allOrgs;

export const getSeatRole = (state: AppState): Role => state.fullstory.seatRole;

export const getIsViewOnly = createSelector(getSeatRole, seatRole => seatRole === Role.VIEW_ONLY);

export const getOrgUseWeeklyEveryone = (state: AppState): boolean =>
  state.fullstory.orgUseWeeklyEveryone;

export const getRetentionTimestamp = (state: AppState): number =>
  state.fullstory.retentionTimestamp;

export const getPlaybackRetentionTimestamp = (state: AppState): number =>
  state.fullstory.playbackRetentionTimestamp;

export const getUmbrellaId = (state: AppState): string => state.fullstory.umbrellaId;

export const getOrgSnippet = (state: AppState): string => state.fullstory.snippet;

export const getAvailableGeoZones = (state: AppState) => state.fullstory.availableGeoZones;

export const getAvailableGeoZonesSorted = createSelector(
  getAvailableGeoZones,
  (zones: GeoZone[]) => {
    // partition to list country zones first, then regions
    const { countries, regions } = zones.reduce<{ countries: GeoZone[]; regions: GeoZone[] }>(
      (acc, zone) => {
        if (zone.Type === ZoneType.CountryType) {
          acc.countries.push(zone);
        } else {
          acc.regions.push(zone);
        }

        return acc;
      },
      { countries: [], regions: [] },
    );

    return [...countries, ...regions];
  },
);

export const getFeatureFlags = (state: AppState): FeatureFlags => state.fullstory.featureFlags;

export const getPrivacyFlags = (state: AppState) => state.fullstory.privacyFlags;
export const getDynamicFilteredTimeRanges = createSelector(
  getPrivacyFlags,
  (flags: PrivacyFlags) => flags.DynamicFilteredTimeRanges,
);

export const getAcceptedTermsVersion = (state: AppState) =>
  state.fullstory.termsAndConditions.acceptedVersion;

export const hasAcceptedTerms = (state: AppState) => {
  const { currentVersion, acceptedVersion } = state.fullstory.termsAndConditions;

  return acceptedVersion >= currentVersion;
};

export const hasRecordedSessions = (state: AppState): boolean =>
  state.fullstory.hasRecordedSessions;

export const getShareSessionEnabled = createSelector(
  getIsViewOnly,
  (state: AppState) => state.fullstory.shareSessionEnabled,
  (isViewOnly, shareSessionEnabled) => (isViewOnly ? false : shareSessionEnabled),
);

export const isDebugMode = (state: AppState) => state.fullstory.debug;
export const isTestEnv = (state: AppState) => state.fullstory.orgId === 'testHarness';

export const getInactivityTimeout = (state: AppState) => state.fullstory.inactivityTimeout;

export const getNotificationsLastSeen = (state: AppState) => state.fullstory.notificationsLastSeen;

export const getAppearanceMode = (state: AppState) => state.fullstory.appearanceMode;

export const hasSsoRoleMapping = (state: AppState) => state.fullstory.ssoRoleMapping;

export const orgPlatform = (state: AppState) => state.fullstory.orgPlatform;

export const getIsDataDirect = (state: AppState) =>
  state.fullstory.orgBasePlan === BasePlan.DATA_PLAN;

export const getAnnotationsSettings = (state: AppState) => state.fullstory.annotationsSettings;
